'use client';

import React, {
    type HTMLAttributes,
    type ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react';
import { buttonVariants } from '@paypoteam/ui-components';

import { cn } from '@/lib/utils';

interface ShowMoreProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
    lines?: number;
    buttonPrefix?: ReactNode;
    showMoreLabel?: string;
    showLessLabel?: string;
    buttonClassName?: string;
}

export default function ShowMoreContent({
    children,
    lines = 1,
    buttonPrefix,
    showMoreLabel = 'Rozwiń',
    showLessLabel = 'Zwiń',
    className,
    buttonClassName,
}: ShowMoreProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const [lineHeight, setLineHeight] = useState(0);

    const contentId = useRef(
        `show-more-content-${Math.random().toString(36).substring(2, 11)}`,
    );

    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!contentRef.current) return;

        const computedStyle = window.getComputedStyle(contentRef.current);
        const lh = parseFloat(computedStyle.lineHeight);

        setLineHeight(lh);

        const maxHeight = lh * lines;
        const actualHeight = contentRef.current.scrollHeight;

        setIsTruncated(actualHeight > maxHeight);
    }, [children, lines]);

    return (
        <div>
            <div
                id={contentId.current}
                ref={contentRef}
                aria-live="polite"
                style={{
                    maxHeight:
                        !isExpanded && isTruncated
                            ? `${lineHeight * lines}px`
                            : 'none',
                }}
                className={cn('overflow-hidden', className)}
            >
                {children}
            </div>

            {isTruncated && (
                <>
                    {buttonPrefix}

                    <button
                        type="button"
                        className={cn(buttonVariants, buttonClassName)}
                        aria-expanded={isExpanded}
                        data-test-id="show-more-button"
                        aria-controls={contentId.current}
                        aria-label={`Kliknij żeby ${isExpanded ? 'zwinąć' : 'rozwinąć'} treść.`}
                        onClick={event => {
                            event.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                    >
                        {isExpanded ? showLessLabel : showMoreLabel}
                    </button>
                </>
            )}
        </div>
    );
}
